import { useState } from 'react';

import Line from '@root/src/components/Line';
import Modal from '@root/src/components/Modal';
import type { Item, YotpoAlbumImage } from '@root/src/types';
import { hasCurrentBlockOutDatesByType, getVisualizerQueryString } from '@root/src/utils/utils';
import { useCustomerEventList } from '@root/src/components/CustomerEventList';
import CustomizeBox from '@root/src/components/CustomizeBox';

const getDefaultTrackerClass = (_: YotpoAlbumImage) => {
  return `tracker-image-unknown_page-yotpo_lightbox-20250212-113241`;
};

type YotpoImageGalleryProps = {
  album: YotpoAlbumImage[];
  item?: Item;
  heading?: string;
  makeTrackerClass?: (image: YotpoAlbumImage) => string;
};

const YotpoImageGallery = (props: YotpoImageGalleryProps) => {
  const { album, item, heading, makeTrackerClass = getDefaultTrackerClass } = props;

  const [selectedImage, setSelectedImage] = useState<YotpoAlbumImage | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  const { primary: primaryEvent } = useCustomerEventList();

  const openSelectedImage = (image: YotpoAlbumImage) => {
    if (typeof window !== 'undefined') {
      setScrollPosition(window.scrollY);
    }

    setSelectedImage(image);
  };

  let isBundle = false,
    isBlockedOut = false,
    isComingSoonBlockedOut = false;

  if (item) {
    isBundle = item.category?.toLowerCase() === 'preconfigured';

    isBlockedOut = hasCurrentBlockOutDatesByType(item, 'general', primaryEvent?.startDate ?? new Date());

    isComingSoonBlockedOut = hasCurrentBlockOutDatesByType(item, 'coming-soon', primaryEvent?.startDate ?? new Date());
  }

  return (
    <>
      <div className="m-section">
        {heading && (
          <div className="mx-auto my-32 w-full max-w-screen-lg px-16 xs:px-32">
            <h2 className="text-h3">{heading}</h2>

            <Line />
          </div>
        )}

        <div className="scrolling-touch scrollbar-none relative flex max-w-full items-end justify-start overflow-x-scroll">
          {album.map((post, key) => (
            <div
              key={key}
              className={`mb-32 h-auto pl-16 last:pr-32 md:first:pl-32 lg:first:ml-[calc((100vw-1280px)/2)]`}
            >
              <img
                loading="lazy"
                width={150}
                src={post.image_url}
                alt=""
                className={`max-w-[30vw] cursor-zoom-in ${makeTrackerClass(post)}`}
                onClick={() => openSelectedImage(post)}
              />
            </div>
          ))}
        </div>
      </div>

      <Modal
        isOpen={selectedImage !== null}
        scrollPosition={scrollPosition}
        onClose={() => setSelectedImage(null)}
        className={`flex min-h-full items-center justify-center py-32 px-0`}
      >
        {selectedImage && (
          <div className="animation-scaleIn max-w[calc(100vw-64px)] container">
            <div className="shadow-3 sm:grid sm:grid-cols-12">
              <div className="col-span-8">
                <img
                  src={selectedImage!.original_image_url}
                  alt=""
                  className="block h-full max-h-[90vh] w-full cursor-zoom-out object-cover transition-opacity duration-200 ease-in-out"
                />
              </div>

              <div className="col-span-4 flex flex-col justify-center p-32">
                {item && (
                  <>
                    <div className="flex-grow" />

                    <h2 className="text-h3-display">{item.display_name}</h2>

                    <CustomizeBox
                      item={item}
                      isBlockedOut={isBlockedOut}
                      isComingSoonBlockedOut={isComingSoonBlockedOut}
                      builderQueryString={getVisualizerQueryString(item, isBundle)}
                      isBundle={isBundle}
                      showOnMobile
                    />
                  </>
                )}

                <div className="flex-grow" />

                {selectedImage?.post?.username && <h3 className="text-h5">@{selectedImage.post.username}</h3>}
                {selectedImage?.post?.content && (
                  <p className="text-sm" style={{ whiteSpace: 'break-spaces' }}>
                    {selectedImage.post.content}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default YotpoImageGallery;
